/**
 * Handles OAuth callback from agents.
 * Exchanges Firebase token with MCP server and redirects back to the agent.
 */

import { type JSX, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
  Select,
  SelectItem,
} from '@heroui/react';
import { AlertCircle } from 'lucide-react';

import { getMyOrganizations } from '~/services/api/organizations';

import { Feedback } from '~/components/Feedback';
import { Logo } from '~/components/Logo';

import type { Organization } from '~/types';

import { LoadingSpinner } from '../components/LoadingSpinner';
import { useAuth } from '../hooks/useAuth';
import { logger } from '../utils/logger';

type AuthStep = 'loading' | 'confirming' | 'processing' | 'success' | 'error';

export function MCPAuthCallback(): JSX.Element {
  const { loading: authLoading, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<AuthStep>('loading');
  const [error, setError] = useState<string>('');
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');

  // Extract URL parameters
  const authCode = useMemo(() => searchParams.get('auth_code'), [searchParams]);
  const redirectUri = useMemo(() => searchParams.get('redirect_uri'), [searchParams]);
  const stateParam = useMemo(() => searchParams.get('state'), [searchParams]);

  useEffect(() => {
    const initAuth = async (): Promise<void> => {
      // Wait for auth to load
      if (authLoading) return;

      // Check if user is authenticated
      if (!user) {
        const returnUrl = `/mcp-auth?${searchParams.toString()}`;
        logger.info(
          { component: 'MCPAuthCallback' },
          'User not authenticated, redirecting to login',
          {
            returnUrl,
          },
        );
        void navigate(`/login?returnTo=${encodeURIComponent(returnUrl)}`);
        return;
      }

      try {
        // Validate required parameters
        if (!authCode || !redirectUri || !stateParam) {
          throw new Error('Missing required OAuth parameters (auth_code, redirect_uri, or state)');
        }

        // Fetch user's organizations
        logger.info({ component: 'MCPAuthCallback' }, 'Fetching user organizations');
        const orgs = await getMyOrganizations();
        setOrganizations(orgs);

        if (orgs.length === 0) {
          throw new Error(
            'You must be a member of at least one organization to use the MCP server.',
          );
        }

        // Default to first organization
        setSelectedOrgId(orgs[0].id);
        setStep('confirming');
      } catch (err) {
        logger.error(
          { component: 'MCPAuthCallback' },
          'Init auth error',
          err instanceof Error ? err : new Error(String(err)),
        );
        setStep('error');
        setError(err instanceof Error ? err.message : 'Failed to initialize authentication');
      }
    };

    void initAuth();
  }, [user, authLoading, authCode, redirectUri, stateParam, searchParams, navigate]);

  const handleAuthorize = async (): Promise<void> => {
    if (!user || !authCode || !redirectUri || !stateParam || !selectedOrgId) return;

    setStep('processing');
    try {
      logger.info({ component: 'MCPAuthCallback' }, 'Getting Firebase tokens');

      // Get Firebase ID token and refresh token from current user.
      // Note: user.refreshToken can be empty for OAuth (Google/GitHub) in some flows—
      // Firebase does not always expose it on the User object for OAuth providers.
      // When empty, MCP cannot refresh the ID token and sessions expire in 1 hour.
      const firebaseToken = await user.getIdToken();
      const refreshToken = user.refreshToken || '';
      if (!refreshToken) {
        logger.warn(
          { component: 'MCPAuthCallback' },
          'No refresh token on user — MCP sessions will expire in 1 hour and you will need to re-authenticate',
          { providerId: user.providerData[0]?.providerId },
        );
      }

      const mcpBaseUrl = import.meta.env['VITE_MCP_BASE_URL'] || 'http://localhost:8000';

      logger.info({ component: 'MCPAuthCallback' }, 'Sending tokens to MCP server', {
        mcpBaseUrl,
        organization_id: selectedOrgId,
      });

      const response = await fetch(`${mcpBaseUrl}/oauth/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          auth_code: authCode,
          firebase_token: firebaseToken,
          refresh_token: refreshToken,
          organization_id: selectedOrgId,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`MCP server error: ${errorText}`);
      }

      const result = await response.json();
      logger.info(
        { component: 'MCPAuthCallback' },
        'Token exchange successful, redirecting to agent',
        {
          redirectUri: result.redirect_uri,
        },
      );

      const redirectUrl = `${result.redirect_uri}?code=${result.code}&state=${encodeURIComponent(result.state ?? '')}`;
      setStep('success');
      void navigate(`/oauth/redirect?next=${encodeURIComponent(redirectUrl)}`, { replace: true });
    } catch (err) {
      logger.error(
        { component: 'MCPAuthCallback' },
        'Token exchange error',
        err instanceof Error ? err : new Error(String(err)),
      );
      setStep('error');
      setError(err instanceof Error ? err.message : 'An error occurred during token exchange');
    }
  };

  if (step === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Feedback
          icon={<LoadingSpinner />}
          title="Preparing Authorization"
          description="Connecting to your account..."
          type={null}
        />
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="flex gap-3">
            <AlertCircle className="text-danger size-6" />
            <p className="text-lg font-bold">Authentication Failed</p>
          </CardHeader>
          <Divider />
          <CardBody>
            <p className="text-default-500">{error}</p>
          </CardBody>
          <CardFooter className="flex justify-end gap-2">
            <Button variant="flat" onPress={() => void navigate('/')}>
              Dashboard
            </Button>
            <Button color="primary" onPress={() => window.location.reload()}>
              Try Again
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (step === 'processing') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Feedback
          icon={<LoadingSpinner />}
          title="Completing Authorization"
          description="Exchanging tokens with MCP server..."
          type={null}
        />
      </div>
    );
  }

  // Step: Confirming
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="flex flex-col items-center gap-4 p-6 italic">
          <Logo />
          <p className="text-default-500 text-center text-sm">
            An application is requesting access to your Compounds account.
          </p>
        </CardHeader>
        <Divider />
        <CardBody className="p-6 space-y-4">
          <div className="space-y-2">
            <p className="text-sm font-medium">Select Organization</p>
            <p className="text-xs text-default-400 mb-2">
              The application will have access to projects and tasks within this organization.
            </p>
            {organizations.length > 1 ? (
              <Select
                label="Organization"
                placeholder="Select an organization"
                selectedKeys={[selectedOrgId]}
                onSelectionChange={keys => setSelectedOrgId(Array.from(keys)[0] as string)}
                variant="bordered"
              >
                {organizations.map(org => (
                  <SelectItem key={org.id} textValue={org.name}>
                    {org.name}
                  </SelectItem>
                ))}
              </Select>
            ) : (
              <div className="p-3 bg-default-100 rounded-lg border border-default-200">
                <p className="font-semibold">{organizations[0]?.name}</p>
                <p className="text-xs text-default-400">Single organization available</p>
              </div>
            )}
          </div>

          <div className="p-3 bg-warning/10 rounded-lg space-y-1">
            <p className="text-xs font-bold text-warning-600 uppercase">Permissions</p>
            <ul className="text-xs text-default-600 list-disc list-inside space-y-1">
              <li>View your profile information</li>
              <li>Read and write projects and tasks</li>
              <li>Execute tool calls in your context</li>
            </ul>
          </div>
        </CardBody>
        <CardFooter className="p-6 flex flex-col gap-3">
          <Button
            color="primary"
            className="w-full font-bold"
            size="lg"
            onPress={() => void handleAuthorize()}
          >
            Authorize
          </Button>
          <Button variant="light" className="w-full" onPress={() => void navigate('/')}>
            Cancel
          </Button>
          <p className="text-[10px] text-center text-default-400">
            By clicking Authorize, you allow this application to access your data as described
            above.
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
